import {
  Box,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Text
} from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useDeactivateWorkspaceMember } from '@/queries/workspaceQueries'
import { showToast } from '@/theme/notifications'
import { Workspace, WorkspaceMember } from '@/types/workspace'

type DeactivateMemberModalProps = {
  opened: boolean
  member?: WorkspaceMember
  workspace: Workspace
  onClose: () => void
}

export const DeactivateMemberModal = ({
  opened,
  member,
  workspace,
  onClose
}: DeactivateMemberModalProps) => {
  const intl = useIntl()
  const { mutateAsync: updateMember, isPending } =
    useDeactivateWorkspaceMember()

  const handleUpdateMember = async () => {
    if (!member) {
      return
    }

    try {
      await updateMember({
        workspaceId: workspace.id,
        memberId: member.id
      })

      showToast(
        intl.formatMessage({ id: 'workspace.members.deactivate.success' }),
        'success'
      )
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'workspace.members.deactivate.error' })
        ),
        'error'
      )
    }

    onClose()
  }

  return (
    <Modal
      size="lg"
      opened={opened}
      title={<FormattedMessage id="workspace.members.deactivate" />}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        <Stack>
          {member && (
            <Text size="sm" mb="lg">
              <FormattedMessage
                id="workspace.members.deactivate.areYouSure"
                values={{
                  memberName: <strong>{member.user.full_name}</strong>,
                  workspaceName: <strong>{workspace.name}</strong>
                }}
              />
            </Text>
          )}

          <Group justify="end">
            <Button miw={120} variant="default" onClick={onClose}>
              <FormattedMessage id="cancel" />
            </Button>

            <Button
              type="submit"
              color="red"
              onClick={() => void handleUpdateMember()}
            >
              <FormattedMessage id="workspace.members.deactivate" />
            </Button>
          </Group>
        </Stack>
      </Box>
    </Modal>
  )
}
